/**
 * タイル配列と、その上での基本判定。
 * ここは DOM も Canvas も知らない（CLAUDE.md §4.4）。
 */

/** 1タイルの論理サイズ(px)。描画倍率はここではなくカメラ側の責任。 */
export const TILE = 32;

export const EMPTY = 0;
/** 既存の壁・床・瓦礫。破壊不可（§3.1） */
export const STRUCTURE = 1;
/** プレイヤー設置物（段ボール箱） */
export const PLACED = 2;
/** プレイヤー設置物（棚）。箱より丈夫（§3.7 オフィスの報酬） */
export const SHELF = 3;
/**
 * 廃ビルに置き去りにされている物（§3.4）。固いので足場になる。
 * プレイヤーは壊せない。猫が勝手にじゃれて壊し、資材になる（§2.3）。
 */
export const SALVAGE = 4;
/**
 * キャットタワーのような、複数マスにまたがる特殊設置物（§3.4 の足場系の上位）。
 * どのマスがどの構造物のものかは grid.build が持つ。撤去はまるごと。
 */
export const BUILT = 5;

export type TileKind =
  | typeof EMPTY
  | typeof STRUCTURE
  | typeof PLACED
  | typeof SHELF
  | typeof SALVAGE
  | typeof BUILT;

/**
 * 猫が爪を立てて登れるマスか。
 * キャットタワーだけ。廃墟の壁や箱の柱は登れない（§3.2 の非対称はそのまま）。
 */
export function isClimbable(grid: Grid, c: number, r: number): boolean {
  return tileAt(grid, c, r) === BUILT;
}

/** プレイヤーが置いた足場か（＝どけられる） */
export function isPlayerPlaced(grid: Grid, c: number, r: number): boolean {
  const t = tileAt(grid, c, r);
  return t === PLACED || t === SHELF || t === BUILT;
}

/**
 * 家具（快適系）はタイルとは別のレイヤーに置く。
 * 足場系（箱・棚・板）は固くて跳躍グラフを変えるが、
 * 快適系（寝床など）は猫が乗るだけで通行の邪魔をしない（§3.4 の2分類）。
 */
export const NO_FURNITURE = 0;
export const BED = 1;
/** 爪とぎ。猫はここで爪をとぐので、周りの設置物が傷みにくくなる（§3.7 食堂の報酬） */
export const POST = 2;

export interface Grid {
  cols: number;
  rows: number;
  /** 長さ cols*rows。JSONにそのまま載る形（§4.4） */
  tiles: number[];
  /** 同じ長さ。家具（0=なし / 1=寝床）。跳躍グラフには影響しない */
  furniture: number[];
  /** 同じ長さ。置き去りの物の種類（0=なし / 1〜=salvage.json の並び） */
  salvage: number[];
  /**
   * 同じ長さ。設置物の傷み具合（§3.5）。
   * 溜まると壊れて資材に戻る。壊れる前に見た目でわかるようにしてあるので、
   * プレイヤーは「ここはよく壊れる＝混んでいる」を統計画面なしで読める。
   */
  wear: number[];
  /** 同じ長さ。複数マスの構造物の id（0=なし）。撤去はこの単位でまとめて行う */
  build: number[];
  /**
   * 同じ長さ。壁に飾った物（0=なし / 1〜=items.json の並び）。
   * 断面図の**奥の壁**に掛かっているので、支えは要らないし通行にも一切影響しない。
   * 跳躍グラフはこの層を見ない（§10.19 猫らしさ・飾りは進行に効かせない）。
   */
  decor: number[];
}

export function decorAt(grid: Grid, c: number, r: number): number {
  if (!inBounds(grid, c, r)) return 0;
  return grid.decor[keyOf(grid, c, r)];
}

/** 壁に飾れるか。空いていて、まだ何も掛かっていないこと */
export function canPlaceDecor(grid: Grid, c: number, r: number): boolean {
  return inBounds(grid, c, r) && tileAt(grid, c, r) === EMPTY && decorAt(grid, c, r) === 0;
}

export function buildIdAt(grid: Grid, c: number, r: number): number {
  if (!inBounds(grid, c, r)) return 0;
  return grid.build[keyOf(grid, c, r)];
}

export function keyOf(grid: Grid, c: number, r: number): number {
  return r * grid.cols + c;
}
export function colOf(grid: Grid, key: number): number {
  return key % grid.cols;
}
export function rowOf(grid: Grid, key: number): number {
  return (key / grid.cols) | 0;
}
export function inBounds(grid: Grid, c: number, r: number): boolean {
  return c >= 0 && c < grid.cols && r >= 0 && r < grid.rows;
}

export function tileAt(grid: Grid, c: number, r: number): number {
  if (!inBounds(grid, c, r)) return STRUCTURE; // 画面外は固い扱い（プロト踏襲）
  return grid.tiles[keyOf(grid, c, r)];
}

export function setTile(grid: Grid, c: number, r: number, v: number): void {
  if (!inBounds(grid, c, r)) return;
  grid.tiles[keyOf(grid, c, r)] = v;
}

export function isSolid(grid: Grid, c: number, r: number): boolean {
  return tileAt(grid, c, r) !== EMPTY;
}

/** ノード＝猫が立てるマス：空いていて、真下が固い（§9） */
export function isNode(grid: Grid, c: number, r: number): boolean {
  return !isSolid(grid, c, r) && isSolid(grid, c, r + 1);
}

/** 設置できるか：空きマスで、支えがある（§3.4 宙には浮かない） */
export function canPlace(grid: Grid, c: number, r: number): boolean {
  return inBounds(grid, c, r) && tileAt(grid, c, r) === EMPTY && isSolid(grid, c, r + 1);
}

export function furnitureAt(grid: Grid, c: number, r: number): number {
  if (!inBounds(grid, c, r)) return NO_FURNITURE;
  return grid.furniture[keyOf(grid, c, r)];
}

export function salvageAt(grid: Grid, c: number, r: number): number {
  if (!inBounds(grid, c, r)) return 0;
  return grid.salvage[keyOf(grid, c, r)];
}

export function wearAt(grid: Grid, c: number, r: number): number {
  if (!inBounds(grid, c, r)) return 0;
  return grid.wear[keyOf(grid, c, r)];
}

/** 快適系を置けるか：猫が立てるマスで、まだ何も置かれていない */
export function canPlaceFurniture(grid: Grid, c: number, r: number): boolean {
  return isNode(grid, c, r) && furnitureAt(grid, c, r) === NO_FURNITURE;
}

/** ノードの足元の中心（論理px）。y は足が着く高さ。 */
export function nodePos(grid: Grid, key: number): { x: number; y: number } {
  return {
    x: colOf(grid, key) * TILE + TILE / 2,
    y: (rowOf(grid, key) + 1) * TILE,
  };
}
